<script lang="ts" setup>
/**
 * TimeControls - 시간 제어 버튼 그룹
 *
 * 버튼 순서: [뒤로] [일시정지/재생] [앞으로] [새로고침]
 */
import { computed } from 'vue';
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { isExceededServerCurrentTime } from '@/common/api/time';
import type { TimePeriodInfo } from '../types';
import type { TimezoneName } from '@/common/utils';
import {
  getForwardTimeRange,
  getBackTimeRange,
  formatTimeRangeToUtc,
  getFormalPeriod,
  getCurrentTimeRange,
} from '../utils';
import XmIcon from '@/common/components/molecules/XmIcon/XmIcon.vue';

interface Props {
  modelValue: TimePeriodInfo;
  disabled?: boolean;
  /** 뒤로/앞으로 버튼 표시 여부 */
  showNavigator?: boolean;
  /** 새로고침 버튼 표시 여부 */
  showRefresh?: boolean;
  /** 새로고침 버튼 비활성화 여부 */
  disableRefresh?: boolean;
  /** true인 경우 일시정지 버튼은 항상 play 아이콘 (PERIOD 모드용) */
  alwaysShowPlay?: boolean;
  /** 시간 범위 최대 제한 (초) */
  maxTimeRangeSecLimit?: number;
  /** true인 경우 Resume 시 modelValue 업데이트 없이 resume 이벤트만 emit */
  preventAutoResume?: boolean;
  /** 일시정지 버튼 표시 여부 */
  showPause?: boolean;
  /** 시간 계산 기준 타임존 */
  timezone?: TimezoneName;
}

interface Emits {
  (e: 'update:modelValue', value: TimePeriodInfo): void;
  (e: 'backward'): void;
  (e: 'forward'): void;
  (e: 'exceeded-current-time'): void;
  (e: 'pause'): void;
  (e: 'resume'): void;
  (e: 'refresh'): void;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  showNavigator: true,
  showRefresh: false,
  disableRefresh: false,
  alwaysShowPlay: false,
  maxTimeRangeSecLimit: undefined,
  preventAutoResume: false,
  showPause: true,
  timezone: undefined,
});

const emit = defineEmits<Emits>();

// ============================================================
// Computed
// ============================================================

const isPerformanceAnalysis = computed(() => props.modelValue.timePeriod === 'pa');
const isDisabled = computed(() => props.disabled || isPerformanceAnalysis.value);
const isPaused = computed(() => props.modelValue.isPaused);

/** 앞으로 버튼 비활성화: Live 모드에서도 활성화 (현재 시간으로 새로고침) */
const isForwardDisabled = computed(() => isDisabled.value);

/** 일시정지 버튼 비활성화 */
const isPauseDisabled = computed(() => isDisabled.value || props.alwaysShowPlay);

/** 일시정지 버튼 아이콘 */
const pauseIcon = computed(() => {
  if (props.alwaysShowPlay || isPaused.value || isPerformanceAnalysis.value) {
    return 'icon-fill-arrow-right';
  }
  return 'icon-pause';
});

// ============================================================
// Handlers
// ============================================================

const handleBackward = () => {
  if (isDisabled.value) return;

  const period = getFormalPeriod(props.modelValue.timePeriod, props.maxTimeRangeSecLimit);
  const { fromTime, toTime } = props.modelValue;

  const { fromTime: backFromTime, toTime: backToTime } = getBackTimeRange({
    fromTime,
    toTime,
    maxTimeRangeSecLimit: props.maxTimeRangeSecLimit,
    timezone: props.timezone,
  });

  emit('update:modelValue', {
    timePeriod: period,
    fromTime: backFromTime,
    toTime: backToTime,
    ...formatTimeRangeToUtc({
      fromTime: backFromTime,
      toTime: backToTime,
      timezone: props.timezone,
    }),
    isPaused: true,
  });

  emit('backward');
};

const handleForward = async () => {
  if (isForwardDisabled.value) return;

  const period = getFormalPeriod(props.modelValue.timePeriod, props.maxTimeRangeSecLimit);

  // Live 모드일 때는 현재 시간으로 새로고침
  if (!isPaused.value) {
    const { fromTime, toTime } = getCurrentTimeRange({ period, timezone: props.timezone });

    emit('update:modelValue', {
      timePeriod: period,
      fromTime,
      toTime,
      ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
      isPaused: false,
    });

    emit('forward');
    return;
  }

  // Pause 모드일 때는 시간 범위 앞으로 이동
  const { fromTime, toTime } = props.modelValue;

  const { fromTime: forwardFromTime, toTime: forwardToTime } = getForwardTimeRange({
    fromTime,
    toTime,
    maxTimeRangeSecLimit: props.maxTimeRangeSecLimit,
    timezone: props.timezone,
  });

  // 현재 시간 초과 시 Live 모드로 복귀
  if (await isExceededServerCurrentTime({ time: forwardToTime })) {
    emit('exceeded-current-time');
    return;
  }

  emit('update:modelValue', {
    timePeriod: period,
    fromTime: forwardFromTime,
    toTime: forwardToTime,
    ...formatTimeRangeToUtc({
      fromTime: forwardFromTime,
      toTime: forwardToTime,
      timezone: props.timezone,
    }),
    isPaused: true,
  });

  emit('forward');
};

const handlePauseToggle = () => {
  if (isPauseDisabled.value) return;

  const newIsPaused = !isPaused.value;

  // Resume 시 preventAutoResume이 설정되어 있으면 이벤트만 emit (부모가 처리)
  if (!newIsPaused && props.preventAutoResume) {
    emit('resume');
    return;
  }

  const { fromTime, toTime, timePeriod } = props.modelValue;

  emit('update:modelValue', {
    timePeriod,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
    isPaused: newIsPaused,
  });

  if (newIsPaused) {
    emit('pause');
  } else {
    emit('resume');
  }
};

const handleRefresh = () => {
  if (isDisabled.value) return;

  const period = props.modelValue.timePeriod;
  const { fromTime, toTime } = getCurrentTimeRange({ period, timezone: props.timezone });

  emit('update:modelValue', {
    timePeriod: period,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
    isPaused: true,
  });

  emit('refresh');
};

// ============================================================
// Expose
// ============================================================

defineExpose({
  backward: handleBackward,
  forward: handleForward,
  togglePause: handlePauseToggle,
  refresh: handleRefresh,
  isPaused,
});
</script>

<template>
  <div class="time-controls">
    <!-- 뒤로 버튼 -->
    <base-button
      v-if="showNavigator"
      type="icon"
      shape="border"
      :disabled="isDisabled"
      @click="handleBackward"
    >
      <xm-icon
        name="icon-fill-2arrow-left"
        size="large"
      />
    </base-button>

    <!-- 일시정지/재생 버튼 -->
    <base-button
      v-if="showPause"
      type="icon"
      shape="border"
      :disabled="isPauseDisabled"
      @click="handlePauseToggle"
    >
      <xm-icon
        :name="pauseIcon"
        size="large"
      />
    </base-button>

    <!-- 앞으로 버튼 -->
    <base-button
      v-if="showNavigator"
      type="icon"
      shape="border"
      :disabled="isForwardDisabled"
      @click="handleForward"
    >
      <xm-icon
        name="icon-fill-2arrow-right"
        size="large"
      />
    </base-button>

    <!-- 새로고침 버튼 -->
    <base-button
      v-if="showRefresh"
      type="icon"
      shape="border"
      :disabled="isDisabled || props.disableRefresh"
      @click="handleRefresh"
    >
      <xm-icon
        name="icon-refresh"
        size="large"
      />
    </base-button>
  </div>
</template>

<style lang="scss" scoped>
.time-controls {
  display: flex;
  gap: 2px;
  align-items: center;
  height: var(--tc-height, 24px);

  :deep(.ev-button) {
    width: var(--tc-height, 24px) !important;
    height: var(--tc-height, 24px) !important;

    .xm-icon {
      --xm-icon-size: calc(var(--tc-height, 24px) - 4px) !important;
    }
  }
}
</style>
